import { useRef, useState } from 'react'
import { useUpsertGoalsMutation } from '@mote/api'
import { BetaSvg } from '@mote/assets/icons/components/BetaIcon'
import { getResourceFile } from '@mote/helpers/misc'
import type { ImageProps } from '@mote/ui'
import { Block, Button, H3, Image, Link, Text } from '@mote/ui'
import { Popup } from '../popup/popup'
import type { UseLoopsOnboardingTrack } from './useLoopsOnboardingTrack'
import { useLoopsOnboardingTrack } from './useLoopsOnboardingTrack'

const LINE_COUNT = 3
const FONT_SIZE = 15
const LINE_HEIGHT = 1.4

type OnboardingViews = 'teacher' | 'student'

type OnboardingView = {
  title: string
  description: string
  image: Pick<ImageProps, 'src' | 'alt'>
  ctaText: string
}

const ONBOARDING_VIEWS: Record<OnboardingViews, OnboardingView> = {
  teacher: {
    title: 'Introducing Mote Loops',
    description:
      'Simply toggle Loops when leaving a text or voice comment in Google Classroom private comments, to prompt students to respond to your feedback.',
    image: {
      src: getResourceFile('/images/onboarding/loops/teacher_view.svg'),
      alt: 'Teacher loop view'
    },
    ctaText: 'Try it now'
  },
  student: {
    title: 'NEW! Respond to your teacher ',
    description:
      'You can now respond directly to your teacher from within Google Classroom private comments to let them know if you understand their feedback.',
    image: {
      src: getResourceFile('/images/onboarding/loops/student_view.svg'),
      alt: 'Student loop view'
    },
    ctaText: 'Got it'
  }
}

export interface OnboardingLoopsProps {
  isTeacherView: boolean
  parentElement?: HTMLElement
  finalFocusElement?: HTMLElement
  isOpen?: boolean
  onClose: (goalState: string, origin?: string) => void
}

export const OnboardingLoops = ({
  isTeacherView,
  parentElement,
  finalFocusElement,
  isOpen = false,
  onClose
}: OnboardingLoopsProps): JSX.Element => {
  const containerRef = useRef<HTMLElement | undefined>(parentElement)
  const finalFocusRef = useRef<HTMLElement | undefined>(finalFocusElement)
  const ctaRef = useRef<HTMLElement | null>()
  const [isLocalOpen, setIsLocalOpen] = useState<boolean>(
    Boolean(parentElement)
  )
  const upsertGoalsMutation = useUpsertGoalsMutation()
  const track: UseLoopsOnboardingTrack = useLoopsOnboardingTrack()
  const view: OnboardingView =
    ONBOARDING_VIEWS[isTeacherView ? 'teacher' : 'student']

  function handleClose(goalState: string, origin?: string): void {
    parentElement && setIsLocalOpen(false)
    if (goalState === 'dismissed') {
      try {
        upsertGoalsMutation.mutate({
          variables: {
            goalName: 'loopOptIn',
            goalStatus: 'dismissed'
          }
        })
      } catch {}
    }
    onClose(goalState, origin)
  }

  return (
    <Popup
      containerProps={{
        'data-testid': 'loops-onboarding-dialog'
      }}
      isOpen={isLocalOpen || isOpen}
      portalProps={{ containerRef: parentElement ? containerRef : null }}
      onClose={(origin?: string) => handleClose('dismissed', origin)}
      width={535}
      finalFocusRef={finalFocusRef}
      initialFocusRef={ctaRef}
      buttonsProps={{ justify: 'flex-end' }}
      showCloseButton={false}
      closeOnOverlayClick={false}
      closeOnEsc={false}
      title={null}
      content={
        <Block gap={16} spreadX>
          <Image {...view.image} borderRadius={2} width={487} height={269} />
          <H3 fontSize={16}>
            🚀 {view.title} {isTeacherView && <BetaSvg />}
          </H3>
          <Text
            fontWeight={400}
            fontSize={FONT_SIZE}
            color='grey.d2'
            lineHeight={LINE_HEIGHT}
            minH={LINE_COUNT * FONT_SIZE * LINE_HEIGHT}
          >
            {view.description}
            {isTeacherView && (
              <>
                {' '}
                Learn more&nbsp;
                <Link
                  fontWeight='inherit'
                  fontSize='inherit'
                  lineHeight='inherit'
                  to='https://support.mote.com/hc/en-us/articles/7080352481940-What-is-Mote-Loops-and-how-does-it-work-'
                  color='black'
                  target='_blank'
                  underline='always'
                >
                  here
                </Link>
                .
              </>
            )}
          </Text>
        </Block>
      }
      buttons={
        <>
          <Block layout='horizontal' align='center' gap={8}>
            {isTeacherView && (
              <Button
                // @ts-expect-error
                variant='ghost'
                text="I'll try later"
                onClick={() => {
                  track.clickedDismiss()
                  handleClose('dismissed')
                }}
              />
            )}
            <Button
              ref={ctaRef}
              // @ts-expect-error
              variant='primary'
              text={view.ctaText}
              onClick={() => {
                if (isTeacherView) track.clickedTurnOn()
                handleClose('completed')
              }}
            />
          </Block>
        </>
      }
    />
  )
}
